import React, { useState, useMemo, useEffect } from 'react';
import { useAppDispatch } from 'redux/store';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, type ActionEntity } from 'actions';

import { fetchEventInstances, fetchOldestRaisedEventInstance } from '../../../../redux/slices/eventInstancesSlice';
import { RootState } from '../../../../redux/store';

const EventInstances = () => {
  // useParams returns `string | undefined`; the route definition guarantees
  // these are present, but defaulting to '' keeps the type as `string`
  // without a non-null assertion. Missing params would surface as an
  // empty-param API call (the same behaviour as before).
  const { zone_name = '', name = '' } = useParams<{
    zone_name: string;
    name: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [showSidebar, setShowSidebar] = useState(false);

  const {
    items: instances,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    status,
    error,
    config: headerConfig,
    filtersConfig,
    oldestRaised,
    raisedCount,
    oldestRaisedKey,
    oldestRaisedError,
  } = useSelector((state: RootState) => state.eventInstances);

  const eventKey = `${zone_name}/${name}`;
  const activeOldestRaised = oldestRaisedKey === eventKey ? oldestRaised : null;
  const activeRaisedCount = oldestRaisedKey === eventKey ? raisedCount : 0;
  const isOldestOnPage =
    !!activeOldestRaised && (instances ?? []).some((i: any) => String(i.event_instance_id) === String(activeOldestRaised.event_instance_id));

  const userData = useSelector((state: RootState) => state.auth.userData);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updateUserSettings,
    updatePage,
    updateLimit,
    limit,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
    triggerActionRefetch,
  } = useTableController({
    userData,
    pageName: 'event-instances',
    filtersConfig,
    headerConfig,
    fetchData: (payload: any) =>
      fetchEventInstances({
        ...payload,
        zoneName: zone_name,
        eventName: name,
      }),
    columnFormats: { created_at: 'datetime', cleared_at: 'datetime' },
    getFormattedRow: (row: any) => ({
      ...row,
      raised: row.raised === null || row.raised === undefined ? 'null' : String(row.raised),
      clearer_ids: Array.isArray(row.clearer_ids) && row.clearer_ids.length > 0 ? row.clearer_ids.join(', ') : 'null',
      clearer_node_instance_ids:
        Array.isArray(row.clearer_node_instance_ids) && row.clearer_node_instance_ids.length > 0 ? row.clearer_node_instance_ids.join(', ') : 'null',
      clearer_user_ids:
        Array.isArray(row.clearer_user_ids) && row.clearer_user_ids.length > 0
          ? row.clearer_user_ids
              .map((id: number | string, index: number) => {
                const username = row.clearer_usernames?.[index];
                return username ? `${username} / ${id}` : String(id);
              })
              .join(', ')
          : 'null',
    }),
  });

  const refreshOldestRaised = () => {
    if (!zone_name || !name) return;
    dispatch(
      fetchOldestRaisedEventInstance({
        zoneName: zone_name,
        eventName: name,
      }) as any
    );
  };

  // Re-fetch the globally-oldest raised instance on every table load. The oldest
  // query is intentionally queue-wide (not page-local), so it must stay in sync
  // with each table fetch — including silent polls that surface changes made on
  // other pages or by other operators. `instances` only gets a new reference when
  // the table actually fetches (poll tick or user action), so this refreshes once
  // per fetch, not once per render.
  useEffect(() => {
    refreshOldestRaised();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zone_name, name, instances]);

  // Project the current event's raised queue into a single action entity and
  // resolve the queue-level clear actions through the registry. `refresh` runs
  // after a successful clear: it refetches the oldest-raised metadata immediately
  // and triggers the table's delayed refetch, so button state, helper text,
  // count and highlight update right away rather than waiting on the poll.
  const queueEntity: ActionEntity = {
    type: 'event_queue',
    zoneName: zone_name,
    name,
    raisedCount: activeRaisedCount,
    oldestRaisedInstanceId: activeOldestRaised ? activeOldestRaised.event_instance_id : null,
  };
  const clearActions = getActions({
    entities: [queueEntity],
    refresh: () => {
      refreshOldestRaised();
      triggerActionRefetch();
    },
  });

  const columns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const formattedData = useMemo(() => getFormattedData(instances), [instances]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: false,
  });

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <div className="d-flex align-items-center mb-3 flex-shrink-0">
        <Button variant="link" onClick={() => navigate('/automation/events')} className="p-0 me-3 text-decoration-none">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Events
        </Button>
        <h4 className="mb-0">
          Event Instances for <code>{name}</code>
        </h4>
      </div>
      <TableViewLayout
        filtersConfig={filtersConfig}
        filters={filters}
        pageName="event-instances"
        columns={(headerConfig ?? []).map((c: any) => ({
          key: c.header_key,
          label: c.label,
        }))}
        pageSize={limit}
        error={error}
        onFilterChange={setFilterAndSync}
        onResetFilters={resetFilters}
        onPageSizeChange={updateLimit}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={updatePage}
        table={table}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        isLoading={status === 'loading'}
        isSilentRefresh={isSilentRefresh}
        onRefresh={refresh}
        rowClassName={(row: any) =>
          activeOldestRaised && String(row.original?.event_instance_id) === String(activeOldestRaised.event_instance_id) ? 'table-warning' : undefined
        }
        toolbarButtons={
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <ActionButton actions={clearActions} action="clearNextEventInstance" variant="outline-secondary" />
            <ActionButton actions={clearActions} action="clearAllEventInstances" variant="outline-danger" />
            <small className="text-muted">
              {!activeOldestRaised
                ? oldestRaisedError
                  ? "Couldn't determine the oldest raised instance — refresh to retry."
                  : 'No raised instances to clear.'
                : isOldestOnPage
                  ? `The highlighted row (#${activeOldestRaised.event_instance_id}) is the oldest raised instance a consumer would pick up next. `
                  : `The oldest raised instance (#${activeOldestRaised.event_instance_id}) isn't shown in the current view. `}
              {activeOldestRaised && 'These actions apply to the whole raised queue, regardless of the current filters or page.'}
            </small>
          </div>
        }
      />
    </div>
  );
};

export default EventInstances;
